import { useState } from "react";
import { Copy, MessageSquare, Send, Shuffle, Trash2, UserMinus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Btn, Avatar, shortDate } from "../../components/app/kit";
import { PublicPagePanel } from "../../components/app/PublicPagePanel";
import { Field, TextInput, Chips } from "../../components/app/Form";
import { useInviteWebsite, siteLabel } from "../../hooks/useInviteWebsite";
import { InviteQR } from "../../components/app/InviteQR";

const WORDS = ["HOOPS", "COURT", "FAST", "BREAK", "SWISH", "PAINT", "RIM", "DUNK", "GAME", "TEAM"];
const makeCode = () => {
  const a = new Uint32Array(2);
  window.crypto.getRandomValues(a);
  return `${WORDS[a[0] % WORDS.length]}-${String(1000 + (a[1] % 9000))}`;
};
const linkFor = (id) => `${window.location.origin}/invite/${id}`;
const ROLE_LABEL = { owner: "Owner", coach: "Coach", manager: "Manager", player: "Player", parent: "Parent" };
const copy = async (text, msg) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Couldn't copy"); }
};

const ROLES = [["coach", "Coach"], ["manager", "Manager"], ["player", "Player"], ["parent", "Parent"]];
const STAFF = ["coach", "manager"];
const ROLE_HINT = { coach: "Coaches can edit the roster, schedule, stats and highlights.", manager: "Managers run the schedule, attendance and messages. They can't edit the roster, stats or billing on the website. In the GAMEDAY app they join as a coach.", player: "Players see the team and teammates' profiles (view only) and can post highlights to their own profile.", parent: "Parents see the team, teammates' profiles and their player's profile (view only)." };

const InviteForm = ({ tid, onSent }) => {
  const roster = useApi(tid && `/teams/${tid}/players`);
  const players = toList(roster.data, ["players"]);
  const [f, setF] = useState({ email: "", name: "", access_code: makeCode(), role: "coach", player_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return toast.error("Enter a valid email");
    if (f.access_code.trim().length < 6) return toast.error("Access code needs at least 6 characters");
    if (!STAFF.includes(f.role) && !f.player_id) return toast.error(f.role === "parent" ? "Pick their child on the roster" : "Pick the player's roster profile");
    setBusy(true);
    try {
      const { data } = await web.post(`/teams/${tid}/coach-invites`, { ...f, email: f.email.trim(), access_code: f.access_code.trim(), player_id: STAFF.includes(f.role) ? null : f.player_id });
      onSent({ ...data, code: f.access_code.trim() });
      setF((s) => ({ email: "", name: "", access_code: makeCode(), role: s.role, player_id: "" }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <Field label="Invite as">
        <Chips testid="invite-role" options={ROLES} value={f.role} onChange={(v) => setF((s) => ({ ...s, role: v }))} />
        <span className="mt-2 block text-xs text-slate-400">{ROLE_HINT[f.role]}</span>
      </Field>
      {!STAFF.includes(f.role) && (
        <Field label={f.role === "parent" ? "Their child" : "Player profile"}>
          <select data-testid="invite-player-select" value={f.player_id} onChange={set("player_id")} className="w-full rounded-xl border border-[#1E293B] bg-[#0F172A] px-3.5 py-2.5 text-sm text-white outline-none focus:border-[#39FF14]">
            <option value="">{players.length ? "Choose a player…" : "Add players to the roster first"}</option>
            {players.map((p) => <option key={p.id} value={p.id}>{p.jersey ? `#${p.jersey} ` : ""}{p.name}</option>)}
          </select>
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={f.role === "coach" ? "Coach's email" : f.role === "manager" ? "Manager's email" : f.role === "parent" ? "Parent's email" : "Player's email"}><TextInput data-testid="invite-email-input" type="email" value={f.email} onChange={set("email")} placeholder="coach@school.org" /></Field>
        <Field label="Name (optional)"><TextInput data-testid="invite-name-input" value={f.name} onChange={set("name")} placeholder="Coach Rivera" /></Field>
      </div>
      <Field label="Access code you'll send with the link">
        <div className="flex gap-2">
          <TextInput data-testid="invite-code-input" value={f.access_code} onChange={set("access_code")} maxLength={64} className="font-mono-gd" />
          <Btn type="button" variant="ghost" data-testid="invite-code-generate" onClick={() => setF((s) => ({ ...s, access_code: makeCode() }))} className="shrink-0 px-4"><Shuffle className="h-4 w-4" /></Btn>
        </div>
      </Field>
      <Btn type="submit" disabled={busy} data-testid="invite-send-button"><Send className="h-4 w-4" />{busy ? "Creating…" : `Invite ${f.role}`}</Btn>
    </form>
  );
};

const SentCard = ({ sent, onClose }) => {
  const site = useInviteWebsite();
  const { membership } = useAuth();
  const teamName = sent.team_name ?? membership?.team_name;
  const link = linkFor(sent.id);
  const msg = `You're invited to join ${teamName ?? "our team"} on GAMEDAY as a ${sent.role ?? "coach"}${sent.player_name ? ` (${sent.player_name})` : ""}. Open ${link} and enter access code ${sent.code}. Expires ${shortDate(sent.expires_at)}. Learn about GAMEDAY Courtside: ${site}`;
  return (
    <div data-testid="invite-sent-card" className="relative mt-6 space-y-3 rounded-2xl border border-[#39FF14]/40 bg-[#39FF14]/5 p-5">
      <button onClick={onClose} aria-label="Close" data-testid="invite-sent-close" className="absolute right-3 top-3 p-1 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
      <p className="font-display text-xl font-bold uppercase text-white">Invite ready for {sent.email}</p>
      <p className="text-sm text-slate-300">Send both the link and the access code. The code is only shown now.</p>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <TextInput readOnly data-testid="invite-link-output" value={link} onFocus={(e) => e.target.select()} className="font-mono-gd text-xs" />
        <TextInput readOnly data-testid="invite-code-output" value={sent.code} onFocus={(e) => e.target.select()} className="font-mono-gd sm:w-44" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn data-testid="invite-copy-message" onClick={() => copy(msg, "Invite message copied")}><Copy className="h-4 w-4" />Copy message</Btn>
        <a href={`sms:?&body=${encodeURIComponent(msg)}`} data-testid="invite-text-link" className="inline-flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white hover:border-[#39FF14]/50"><MessageSquare className="h-4 w-4" />Text it</a>
        <InviteQR link={link} email={sent.email} teamName={teamName} role={ROLE_LABEL[sent.role] ?? sent.role} code={sent.code} testid="invite-sent-qr" />
      </div>
      <p data-testid="invite-website-line" className="text-xs text-slate-400">The message also includes our website: <a href={site} target="_blank" rel="noreferrer" className="text-white underline decoration-[#39FF14]/60">{siteLabel(site)}</a></p>
      <p className="text-xs text-slate-400">GAMEDAY also emails them an invite. Either way, the code is needed to join from this link.</p>
    </div>
  );
};

export default function TeamAccess() {
  const site = useInviteWebsite();
  const { membership, me } = useAuth();
  const tid = membership?.team_id;
  const access = useApi(tid && `/teams/${tid}/access`, web);
  const [sent, setSent] = useState(null);
  const isOwner = access.data?.my_role === "owner";
  const members = toList(access.data?.members);
  const invites = toList(access.data?.invites);

  const cancel = async (inv) => {
    if (!window.confirm(`Cancel the invite for ${inv.email}?`)) return;
    try { await web.delete(`/teams/${tid}/coach-invites/${inv.id}`); toast.success("Invite cancelled"); access.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from the team? They lose access in the website and the app.`)) return;
    try { await api.delete(`/teams/${tid}/members/${m.id}`); toast.success("Member removed"); access.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Team access" />
      <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
        <div className="space-y-6">
        <PublicPagePanel tid={tid} />
        <Panel>
          <h3 className="mb-1 font-display text-xl font-bold uppercase text-white">Invite to the team</h3>
          <p className="mb-5 text-sm text-slate-400">They open the link, enter your access code, then pick their own password.</p>
          <InviteForm tid={tid} onSent={(s) => { setSent(s); access.reload(); }} />
          {sent && <SentCard sent={sent} onClose={() => setSent(null)} />}
        </Panel>
        </div>
        <div className="space-y-6">
          {access.loading && !access.data ? <Loader /> : access.error ? <ErrorNote error={access.error} /> : (
            <>
              <Panel>
                <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Members</h3>
                <ul className="divide-y divide-[#1E293B]">
                  {members.map((m) => (
                    <li key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 py-3">
                      <Avatar name={m.name || m.email || "?"} size="h-10 w-10" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-white">{m.name || m.email}</p>
                        <p className="truncate text-xs text-slate-400">{m.email} · {ROLE_LABEL[m.web_role ?? m.role] ?? m.role}</p>
                      </div>
                      {isOwner && m.role !== "owner" && m.user_id !== me?.id && (
                        <Btn variant="danger" data-testid={`member-remove-${m.id}`} onClick={() => remove(m)} className="px-3 py-2 text-xs"><UserMinus className="h-3.5 w-3.5" />Remove</Btn>
                      )}
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel>
                <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Pending invites</h3>
                {!invites.length ? <Empty title="No pending invites" /> : (
                  <ul className="divide-y divide-[#1E293B]">
                    {invites.map((i) => (
                      <li key={i.id} data-testid={`invite-${i.id}`} className="flex items-center gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-white">{i.name || i.email}</p>
                          <p className="truncate text-xs text-slate-400">{i.email} · {ROLE_LABEL[i.role] ?? "Coach"}{i.player_name ? ` · ${i.player_name}` : ""} · expires {shortDate(i.expires_at)}</p>
                        </div>
                        <InviteQR compact link={linkFor(i.id)} email={i.email} teamName={membership?.team_name} role={ROLE_LABEL[i.role] ?? "Coach"} testid={`invite-qr-${i.id}`} />
                        <button data-testid={`invite-copy-${i.id}`} onClick={() => copy(`${linkFor(i.id)}\nLearn about GAMEDAY Courtside: ${site}`, "Link copied (send the access code too)")} className="rounded-full p-2 text-slate-400 hover:text-white" aria-label="Copy link"><Copy className="h-4 w-4" /></button>
                        <button data-testid={`invite-cancel-${i.id}`} onClick={() => cancel(i)} className="rounded-full p-2 text-slate-400 hover:text-red-200" aria-label="Cancel invite"><Trash2 className="h-4 w-4" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
